
// ✅ src/pages/VehicleV2/BatteryCardMaintenanceListing.jsx
//
// Production-style design template based on ProductionBOMListing.jsx
//
// Backend mount:
//   app.use("/api/batteryCardMaintenanceListing", batteryCardMaintenanceListing);
//
// APIs:
//   GET  /api/batteryCardMaintenanceListing/init
//   GET  /api/batteryCardMaintenanceListing/list
//   POST /api/batteryCardMaintenanceListing/delete
//   POST /api/batteryCardMaintenanceListing/scrap
//
// Routes:
//   Add  : /vehicle/BatteryCardMaintenanceEntry
//   Edit : /vehicle/BatteryCardMaintenanceEntry?batteryCardMainId=123
//
// ✅ Design updated to Production BOM template
// ✅ Existing Battery Card functionality preserved
// ✅ Pagination preserved
// ✅ Edit / Testing / Scrap preserved
// ✅ Bulk selection / delete preserved
// ✅ Dark mode preserved

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaPlus,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
  FaBatteryFull,
  FaEdit,
  FaTools,
  FaRecycle,
} from "react-icons/fa";

/* =========================================================
   API
========================================================= */

const API_BASE =
  "https://nissi-india.com/api/batteryCardMaintenanceListing";

/* =========================================================
   HELPERS
========================================================= */

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

/* =========================================================
   DEFAULT DROPDOWNS
========================================================= */

const defaultDropdowns = {
  batteryCodes: [
    {
      value: "Select",
      label: "Select",
    },
  ],

  batteryTypes: [
    {
      value: "Select",
      label: "Select",
    },
  ],

  stores: [
    {
      value: "Select",
      label: "Select",
    },
  ],

  vehicleTypes: [
    {
      value: "Select",
      label: "Select",
    },
  ],

  batteryStatus: [
    {
      value: "Process",
      label: "Process",
    },
    {
      value: "Scrapped",
      label: "Scrapped",
    },
  ],
};

/* =========================================================
   EMPTY FILTERS
========================================================= */

const emptyFilters = {
  vehicleName: "",
  vehicleRegNo: "",
  fittedDate: "",
  storeId: "Select",
  batteryCodeId: "Select",
  batteryTypeId: "Select",
  vehicleTypeIndex: "Select",
  batteryStatus: "Process",
};

/* =========================================================
   MAP BACKEND ROW
========================================================= */

const mapRow = (r) => {
  const id =
    r.fld_batterycardmainid ??
    r.batteryCardMainId ??
    r.id ??
    r.fld_id ??
    r.fld_batterycard_id;

  return {
    id: toInt(id),

    store: toText(
      r.fld_storename ??
        r.storename ??
        r.store ??
        r.StoreName
    ),

    vehicleMainType: toText(
      r.fld_vehicletypename ??
        r.fld_vehicle_subtypename ??
        r.vehicleMainType ??
        r.vehiclemaintype ??
        r.vehicleTypeName
    ),

    vehicleCode: toText(
      r.fld_vehicle_code ??
        r.vehicleCode ??
        r.vehiclecode
    ),

    vehicleName: toText(
      r.fld_vehicle_name ??
        r.vehicleName ??
        r.vehiclename
    ),

    vehicleRegistrationNumber: toText(
      r.fld_vehicle_regno ??
        r.vehicleRegNo ??
        r.vehicleRegistrationNumber ??
        r.vehicleregno
    ),

    batteryCode: toText(
      r.fld_batterycode ??
        r.batteryCode ??
        r.batterycode
    ),

    batteryType: toText(
      r.fld_batterytype ??
        r.batteryType ??
        r.batterytype
    ),

    fittedDate: toText(
      r.fld_Fitteddate ??
        r.fld_fitteddate ??
        r.fittedDate ??
        r.fld_fitted_date
    ),

    currentRemoveDate: toText(
      r.currentdate ??
        r.currentRemoveDate ??
        r.fld_currentremovedate ??
        r.fld_remove_date ??
        r.fld_removedate
    ),

    actualLife: toText(
      r.fld_actuallife ??
        r.actualLife ??
        r.actuallife
    ),

    currentLife: toText(
      r.fld_currentlife ??
        r.currentLife ??
        r.currentlife
    ),

    alert: toText(
      r.alert ??
        r.fld_alert ??
        ""
    ),

    batterystatus: toText(
      r.fld_batterystatus ??
        r.batterystatus ??
        ""
    ),
  };
};

/* =========================================================
   COMPONENT
========================================================= */

const BatteryCardMaintenanceListing = () => {
  const navigate = useNavigate();

  /* =======================================================
     THEME
  ======================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const tableHeaderClass = buttonClass;

  /* =======================================================
     SAFE LOCAL STORAGE
  ======================================================= */

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") {
      return fallback;
    }

    const val = localStorage.getItem(key);

    return val !== null && val !== undefined
      ? val
      : fallback;
  };

  /* =======================================================
     STORAGE VALUES
  ======================================================= */

  const finYearId = getLS("finYearId");

  const finYear = getLS("finYear");

  const branchIdLS =
    getLS("branchId") ||
    getLS("branchid");

  const branchTypeLS =
    getLS("branchType") ||
    getLS("branchtype") ||
    "HO";

  const userIdLS =
    getLS("userId") ||
    getLS("loginuserid") ||
    getLS("loginUserId");

  const loginUserIdLS =
    typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId") ||
        userIdLS
      : userIdLS;

  /* =======================================================
     PARSED VALUES
  ======================================================= */

  const branchId = useMemo(
    () => toInt(branchIdLS, 8),
    [branchIdLS]
  );

  const branch = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );

  const userId = useMemo(
    () => String(userIdLS || "").trim(),
    [userIdLS]
  );

  const loginUserId = useMemo(
    () => toInt(loginUserIdLS, 0),
    [loginUserIdLS]
  );

  /* =======================================================
     COMMON HEADERS
  ======================================================= */

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    ]
  );

  /* =======================================================
     FILTER UI
  ======================================================= */

  const [showFilters, setShowFilters] =
    useState(true);

  const [filters, setFilters] =
    useState(emptyFilters);

  /* =======================================================
     DROPDOWNS
  ======================================================= */

  const [dd, setDd] =
    useState(defaultDropdowns);

  /* =======================================================
     GRID
  ======================================================= */

  const [rows, setRows] =
    useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] =
    useState(false);

  const [pageLoading, setPageLoading] =
    useState(false);

  /* =======================================================
     SEARCH STATE
  ======================================================= */

  const [hasSearched, setHasSearched] =
    useState(false);

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [page, setPage] =
    useState(1);

  const [limit, setLimit] =
    useState(10);

  const [total, setTotal] =
    useState(0);

  /* =======================================================
     SELECTION
  ======================================================= */

  const [selectedIds, setSelectedIds] =
    useState([]);

  const [selectAll, setSelectAll] =
    useState(false);

  /* =======================================================
     TOTAL PAGES
  ======================================================= */

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);

    return t > 0 ? t : 1;
  }, [total, limit]);

  /* =======================================================
     TOTAL CURRENT RECORDS
  ======================================================= */

  const totalCurrentRecords = useMemo(
    () => rows.length,
    [rows]
  );

  /* =======================================================
     FILTER CLASSES
  ======================================================= */

  const labelClass = `
    block mb-2 text-sm font-medium
    ${isDark ? "text-gray-300" : "text-gray-700"}
  `;

  const inputClass = `
    w-full px-4 py-2
    border rounded-lg
    focus:ring-2
    focus:ring-blue-500
    focus:border-transparent
    transition-all
    ${
      isDark
        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
    }
  `;

  /* =======================================================
     FILTER HANDLER
  ======================================================= */

  const setFilter = (
    name,
    value
  ) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     INIT LOAD
  ======================================================= */

  const loadInit = useCallback(
    async () => {
      try {
        setPageLoading(true);

        const { data } =
          await axios.get(
            `${API_BASE}/init`,
            {
              params: {
                branchId,
                loginUserId,
                branchType: branch,
              },
              headers: commonHeaders,
            }
          );

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Failed to load initial data"
          );
        }

        setDd({
          batteryCodes:
            Array.isArray(
              data?.dropdowns?.batteryCodes
            )
              ? data.dropdowns.batteryCodes
              : defaultDropdowns.batteryCodes,

          batteryTypes:
            Array.isArray(
              data?.dropdowns?.batteryTypes
            )
              ? data.dropdowns.batteryTypes
              : defaultDropdowns.batteryTypes,

          stores:
            Array.isArray(
              data?.dropdowns?.stores
            )
              ? data.dropdowns.stores
              : defaultDropdowns.stores,

          vehicleTypes:
            Array.isArray(
              data?.dropdowns?.vehicleTypes
            )
              ? data.dropdowns.vehicleTypes
              : defaultDropdowns.vehicleTypes,

          batteryStatus:
            Array.isArray(
              data?.dropdowns?.batteryStatus
            )
              ? data.dropdowns.batteryStatus
              : defaultDropdowns.batteryStatus,
        });

        if (
          data?.defaults?.batteryStatus
        ) {
          setFilters((prev) => ({
            ...prev,
            batteryStatus:
              data.defaults.batteryStatus,
          }));
        }
      } catch (err) {
        console.error(
          "Battery card init error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load dropdowns"
        );
      } finally {
        setPageLoading(false);
      }
    },
    [
      branchId,
      loginUserId,
      branch,
      commonHeaders,
    ]
  );

  /* =======================================================
     LIST LOAD
  ======================================================= */

  const fetchList = useCallback(
    async (
      nextPage = page,
      nextLimit = limit,
      nextFilters = filters,
      isSilent = false
    ) => {
      try {
        if (!isSilent) {
          setLoading(true);
        }

        const params = {
          branchId,

          branchType: branch,

          page: nextPage,

          limit: nextLimit,

          vehicleName:
            toStr(
              nextFilters.vehicleName
            ),

          vehicleRegNo:
            toStr(
              nextFilters.vehicleRegNo
            ),

          batteryCodeId:
            toStr(
              nextFilters.batteryCodeId,
              "Select"
            ) || "Select",

          batteryTypeId:
            toStr(
              nextFilters.batteryTypeId,
              "Select"
            ) || "Select",

          storeId:
            toStr(
              nextFilters.storeId,
              "Select"
            ) || "Select",

          vehicleTypeIndex:
            toStr(
              nextFilters.vehicleTypeIndex,
              "Select"
            ) || "Select",

          batteryStatus:
            toStr(
              nextFilters.batteryStatus,
              "Process"
            ) || "Process",

          fittedDate:
            toStr(
              nextFilters.fittedDate
            ),
        };

        const { data } =
          await axios.get(
            `${API_BASE}/list`,
            {
              params,
              headers: commonHeaders,
            }
          );

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Failed to load list"
          );
        }

        const mapped =
          Array.isArray(data?.rows)
            ? data.rows.map(mapRow)
            : [];

        setRows(mapped);

        setTotal(
          toInt(
            data?.total,
            0
          )
        );

        setPage(
          toInt(
            data?.page,
            nextPage
          )
        );

        setLimit(
          toInt(
            data?.limit,
            nextLimit
          )
        );

        setSelectedIds([]);

        setSelectAll(false);
      } catch (err) {
        console.error(
          "Battery card list error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load list"
        );

        setRows([]);

        setTotal(0);

        setSelectedIds([]);

        setSelectAll(false);
      } finally {
        if (!isSilent) {
          setLoading(false);
        }
      }
    },
    [
      page,
      limit,
      filters,
      branchId,
      branch,
      commonHeaders,
    ]
  );

  /* =======================================================
     INITIAL EFFECTS
  ======================================================= */

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    fetchList(
      1,
      limit,
      filters
    );

    // Initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     SELECT ALL
  ======================================================= */

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);

      setSelectAll(false);

      return;
    }

    const selectableIds =
      rows
        .filter(
          (x) =>
            String(
              x?.batterystatus || ""
            ).toLowerCase() !==
            "scrapped"
        )
        .map((x) => x.id)
        .filter((x) => x > 0);

    setSelectedIds(
      selectableIds
    );

    setSelectAll(
      selectableIds.length > 0
    );
  };

  /* =======================================================
     SINGLE SELECT
  ======================================================= */

  const toggleSelect = (id) => {
    if (!id) return;

    setSelectedIds(
      (prev) =>
        prev.includes(id)
          ? prev.filter(
              (x) => x !== id
            )
          : [...prev, id]
    );
  };

  /* =======================================================
     SELECT ALL SYNC
  ======================================================= */

  useEffect(() => {
    const selectableIds =
      rows
        .filter(
          (x) =>
            String(
              x?.batterystatus || ""
            ).toLowerCase() !==
            "scrapped"
        )
        .map((x) => x.id)
        .filter((x) => x > 0);

    setSelectAll(
      selectableIds.length > 0 &&
        selectableIds.every(
          (id) =>
            selectedIds.includes(id)
        )
    );
  }, [
    rows,
    selectedIds,
  ]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch = () => {
    setPage(1);

    setHasSearched(true);

    fetchList(
      1,
      limit,
      filters
    );
  };

  /* =======================================================
     CLEAR / VIEW ALL
  ======================================================= */

  const handleClearFilters = () => {
    const resetFilters = {
      ...emptyFilters,
    };

    setFilters(
      resetFilters
    );

    setPage(1);

    setHasSearched(true);

    setTimeout(() => {
      fetchList(
        1,
        limit,
        resetFilters
      );
    }, 0);
  };

  /* =======================================================
     ADD NEW
  ======================================================= */

  const handleAddNew = () => {
    navigate(
      "/vehicle/BatteryCardMaintenanceEntry"
    );
  };

  /* =======================================================
     EDIT
  ======================================================= */

  const goEdit = (id) => {
    if (!id) return;

    navigate(
      `/vehicle/BatteryCardMaintenanceEntry?batteryCardMainId=${id}`
    );
  };

  /* =======================================================
     TESTING
  ======================================================= */

  const handleTesting = (
    batteryCardMainId
  ) => {
    if (!batteryCardMainId) return;

    navigate(
      "/vehicle/BatteryTesting",
      {
        state: {
          batteryCardMainId:
            batteryCardMainId,
        },
      }
    );
  };

  /* =======================================================
     DELETE
  ======================================================= */

  const doDelete = useCallback(
    async () => {
      if (
        selectedIds.length === 0
      ) {
        toast.warn(
          "Please select at least one battery card to delete."
        );

        return;
      }

      const ok =
        window.confirm(
          `Are you sure you want to delete ${selectedIds.length} record(s)?`
        );

      if (!ok) return;

      try {
        setLoading(true);

        const { data } =
          await axios.post(
            `${API_BASE}/delete`,
            {
              ids: selectedIds,

              loginUserId,
            },
            {
              headers:
                commonHeaders,
            }
          );

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Delete failed"
          );
        }

        toast.success(
          data?.message ||
            "Deleted successfully!"
        );

        await fetchList(
          page,
          limit,
          filters,
          true
        );
      } catch (err) {
        console.error(
          "Delete error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Delete failed"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      selectedIds,
      loginUserId,
      commonHeaders,
      fetchList,
      page,
      limit,
      filters,
    ]
  );

  /* =======================================================
     SCRAP
  ======================================================= */

  const doScrap = useCallback(
    async (
      batteryCardMainId
    ) => {
      if (!batteryCardMainId) {
        return;
      }

      const ok =
        window.confirm(
          "Are you sure you want to scrap this battery card?"
        );

      if (!ok) return;

      try {
        setLoading(true);

        const { data } =
          await axios.post(
            `${API_BASE}/scrap`,
            {
              batteryCardMainId:
                toInt(
                  batteryCardMainId,
                  0
                ),
            },
            {
              headers:
                commonHeaders,
            }
          );

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Scrap failed"
          );
        }

        toast.success(
          data?.message ||
            "Battery card scrapped successfully"
        );

        await fetchList(
          page,
          limit,
          filters,
          true
        );
      } catch (err) {
        console.error(
          "Scrap error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Scrap failed"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      commonHeaders,
      fetchList,
      page,
      limit,
      filters,
    ]
  );

  /* =======================================================
     PAGINATION
  ======================================================= */

  const goPrev = () => {
    if (page <= 1) return;

    const nextPage =
      Math.max(
        1,
        page - 1
      );

    setPage(nextPage);

    fetchList(
      nextPage,
      limit,
      filters
    );
  };

  const goNext = () => {
    if (
      page >= totalPages
    ) {
      return;
    }

    const nextPage =
      Math.min(
        totalPages,
        page + 1
      );

    setPage(nextPage);

    fetchList(
      nextPage,
      limit,
      filters
    );
  };

  const changeLimit = (
    e
  ) => {
    const nextLimit =
      toInt(
        e.target.value,
        10
      );

    setLimit(
      nextLimit
    );

    setPage(1);

    fetchList(
      1,
      nextLimit,
      filters
    );
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark
            ? "bg-gray-900"
            : "bg-gray-100"
        }`}
      >
        {/* =================================================
            HEADER BAR
        ================================================= */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BATTERY CARD MAINTENANCE
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        {/* =================================================
            MAIN CARD
        ================================================= */}

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h2
                className={`text-2xl font-bold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                Battery Card Maintenance Listing
              </h2>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* ADD NEW */}

              <button
                type="button"
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              {/* FILTER TOGGLE */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    (prev) => !prev
                  )
                }
                disabled={
                  pageLoading ||
                  loading
                }
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* =================================================
              SEARCH FILTERS
          ================================================= */}

          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark
                  ? "bg-gray-800/50"
                  : "bg-blue-50"
              }`}
            >
              {/* FILTER HEADER */}

              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h3
                    className={`text-lg font-semibold ${
                      isDark
                        ? "text-white"
                        : "text-blue-900"
                    }`}
                  >
                    Search Criteria
                  </h3>

                  <p
                    className={`text-sm mt-1 ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    Filter battery cards using the criteria below.
                  </p>
                </div>

                <div className="flex gap-2">
                  {/* CLEAR */}

                  <button
                    type="button"
                    onClick={
                      handleClearFilters
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${
                      loading ||
                      pageLoading
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Clear
                  </button>

                  {/* SEARCH */}

                  <button
                    type="button"
                    onClick={
                      handleSearch
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                      loading ||
                      pageLoading
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />

                    {loading
                      ? "Searching..."
                      : "Search"}
                  </button>
                </div>
              </div>

              {/* FILTER GRID */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* STORE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Store
                  </label>

                  <select
                    value={
                      filters.storeId
                    }
                    onChange={(e) =>
                      setFilter(
                        "storeId",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                  >
                    {(
                      dd.stores || []
                    ).map(
                      (o, i) => (
                        <option
                          key={i}
                          value={
                            o.value
                          }
                        >
                          {o.label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* VEHICLE NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Name
                  </label>

                  <input
                    type="text"
                    value={
                      filters.vehicleName
                    }
                    onChange={(e) =>
                      setFilter(
                        "vehicleName",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter vehicle name"
                  />
                </div>

                {/* VEHICLE NUMBER */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Number
                  </label>

                  <input
                    type="text"
                    value={
                      filters.vehicleRegNo
                    }
                    onChange={(e) =>
                      setFilter(
                        "vehicleRegNo",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter registration number"
                  />
                </div>

                {/* BATTERY CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Battery Code
                  </label>

                  <select
                    value={
                      filters.batteryCodeId
                    }
                    onChange={(e) =>
                      setFilter(
                        "batteryCodeId",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                  >
                    {(
                      dd.batteryCodes ||
                      []
                    ).map(
                      (o, i) => (
                        <option
                          key={i}
                          value={
                            o.value
                          }
                        >
                          {o.label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* BATTERY TYPE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Battery Type
                  </label>

                  <select
                    value={
                      filters.batteryTypeId
                    }
                    onChange={(e) =>
                      setFilter(
                        "batteryTypeId",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                  >
                    {(
                      dd.batteryTypes ||
                      []
                    ).map(
                      (o, i) => (
                        <option
                          key={i}
                          value={
                            o.value
                          }
                        >
                          {o.label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* VEHICLE MAIN TYPE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Main Type
                  </label>

                  <select
                    value={
                      filters.vehicleTypeIndex
                    }
                    onChange={(e) =>
                      setFilter(
                        "vehicleTypeIndex",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                  >
                    {(
                      dd.vehicleTypes ||
                      []
                    ).map(
                      (o, i) => (
                        <option
                          key={i}
                          value={
                            o.value
                          }
                        >
                          {o.label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* FITTED DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Fitted Date
                  </label>

                  <input
                    type="text"
                    value={
                      filters.fittedDate
                    }
                    onChange={(e) =>
                      setFilter(
                        "fittedDate",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    placeholder="dd/MM/yyyy"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* STATUS */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Battery Status
                  </label>

                  <select
                    value={
                      filters.batteryStatus
                    }
                    onChange={(e) =>
                      setFilter(
                        "batteryStatus",
                        e.target.value
                      )
                    }
                    disabled={
                      loading ||
                      pageLoading
                    }
                    className={
                      inputClass
                    }
                  >
                    {(
                      dd.batteryStatus ||
                      []
                    ).map(
                      (o, i) => (
                        <option
                          key={i}
                          value={
                            o.value
                          }
                        >
                          {o.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              RESULTS SUMMARY
          ================================================= */}

          <div
            className={`mb-4 px-4 py-3 rounded-lg border flex items-center justify-between flex-wrap gap-3 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div>
              <span
                className={`text-sm ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                Showing
              </span>

              <span
                className={`ml-2 font-semibold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                {totalCurrentRecords}
              </span>

              <span
                className={`ml-1 text-sm ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                records on this page
              </span>
            </div>

            <div
              className={`text-sm ${
                isDark
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              Total Records:
              <span
                className={`ml-1 font-semibold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                {total}
              </span>
            </div>
          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading ||
            loading ? (
              <div
                className={`p-16 text-center ${
                  isDark
                    ? "bg-gray-800"
                    : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : rows.length ===
              0 ? (
              <div
                className={`p-16 text-center ${
                  isDark
                    ? "bg-gray-800"
                    : "bg-gray-50"
                }`}
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark
                      ? "bg-gray-700"
                      : "bg-gray-100"
                  }`}
                >
                  <FaBatteryFull
                    className={`w-7 h-7 ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  />
                </div>

                <div
                  className={`text-lg font-medium ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  {hasSearched
                    ? "No Records found"
                    : "Please search to view records"}
                </div>

                <p
                  className={`mt-2 text-sm ${
                    isDark
                      ? "text-gray-500"
                      : "text-gray-400"
                  }`}
                >
                  Try changing the search criteria.
                </p>
              </div>
            ) : (
              <>
                <table
                  className={`w-full min-w-[1900px] ${
                    isDark
                      ? "bg-gray-800"
                      : "bg-white"
                  }`}
                >
                  {/* =================================================
                      TABLE HEADER
                  ================================================= */}

                  <thead
                    className={`${tableHeaderClass} text-white`}
                  >
                    <tr>
                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        S.No
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Store
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Vehicle Main Type
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Vehicle Code
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Vehicle Name
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Vehicle Registration Number
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Battery Code
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Battery Type
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Fitted Date
                      </th>

                      <th className="px-4 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        Current / Remove Date
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Actual Life
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Current Life
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Alert
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Edit
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Testing
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap">
                        Scrap
                      </th>

                      <th className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap w-16">
                        <input
                          type="checkbox"
                          checked={
                            selectAll
                          }
                          onChange={
                            handleSelectAll
                          }
                          disabled={
                            rows.filter(
                              (x) =>
                                String(
                                  x?.batterystatus ||
                                    ""
                                ).toLowerCase() !==
                                "scrapped"
                            ).length ===
                            0
                          }
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  {/* =================================================
                      TABLE BODY
                  ================================================= */}

                  <tbody
                    className={`${
                      isDark
                        ? "divide-gray-700"
                        : "divide-gray-200"
                    } divide-y`}
                  >
                    {rows.map(
                      (
                        card,
                        idx
                      ) => {
                        const disabled =
                          String(
                            card.batterystatus ||
                              ""
                          ).toLowerCase() ===
                          "scrapped";

                        const checked =
                          selectedIds.includes(
                            card.id
                          );

                        return (
                          <tr
                            key={
                              card.id ||
                              idx
                            }
                            className={`${
                              idx %
                                2 ===
                              0
                                ? isDark
                                  ? "bg-gray-800"
                                  : "bg-white"
                                : isDark
                                ? "bg-gray-750"
                                : "bg-gray-50"
                            } ${
                              disabled
                                ? "opacity-60"
                                : ""
                            } transition-colors ${
                              disabled
                                ? ""
                                : isDark
                                ? "hover:bg-gray-700"
                                : "hover:bg-blue-50"
                            }`}
                          >
                            {/* S.NO */}

                            <td
                              className={`px-4 py-4 text-sm text-center whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {(page -
                                1) *
                                limit +
                                idx +
                                1}
                            </td>

                            {/* STORE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {card.store ||
                                "-"}
                            </td>

                            {/* VEHICLE MAIN TYPE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.vehicleMainType ||
                                  "-"
                              }
                            </td>

                            {/* VEHICLE CODE */}

                            <td
                              className={`px-4 py-4 text-sm font-medium whitespace-nowrap ${
                                isDark
                                  ? "text-blue-400"
                                  : "text-blue-600"
                              }`}
                            >
                              {
                                card.vehicleCode ||
                                  "-"
                              }
                            </td>

                            {/* VEHICLE NAME */}

                            <td
                              className={`px-4 py-4 text-sm font-medium whitespace-nowrap ${
                                isDark
                                  ? "text-white"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.vehicleName ||
                                  "-"
                              }
                            </td>

                            {/* REGISTRATION */}

                            <td
                              className={`px-4 py-4 text-sm font-medium whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.vehicleRegistrationNumber ||
                                  "-"
                              }
                            </td>

                            {/* BATTERY CODE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.batteryCode ||
                                  "-"
                              }
                            </td>

                            {/* BATTERY TYPE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.batteryType ||
                                  "-"
                              }
                            </td>

                            {/* FITTED DATE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.fittedDate ||
                                  "-"
                              }
                            </td>

                            {/* CURRENT / REMOVE DATE */}

                            <td
                              className={`px-4 py-4 text-sm whitespace-nowrap ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-900"
                              }`}
                            >
                              {
                                card.currentRemoveDate ||
                                  "-"
                              }
                            </td>

                            {/* ACTUAL LIFE */}

                            <td
                              className={`px-4 py-4 text-sm text-center font-semibold whitespace-nowrap ${
                                isDark
                                  ? "text-green-400"
                                  : "text-green-600"
                              }`}
                            >
                              {
                                card.actualLife ||
                                  "-"
                              }
                            </td>

                            {/* CURRENT LIFE */}

                            <td
                              className={`px-4 py-4 text-sm text-center font-semibold whitespace-nowrap ${
                                isDark
                                  ? "text-green-400"
                                  : "text-green-600"
                              }`}
                            >
                              {
                                card.currentLife ||
                                  "-"
                              }
                            </td>

                            {/* ALERT */}

                            <td className="px-4 py-4 text-center whitespace-nowrap">
                              {card.alert ? (
                                <span
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                    isDark
                                      ? "bg-yellow-900/50 text-yellow-300"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {
                                    card.alert
                                  }
                                </span>
                              ) : (
                                <span
                                  className={
                                    isDark
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }
                                >
                                  -
                                </span>
                              )}
                            </td>

                            {/* EDIT */}

                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  goEdit(
                                    card.id
                                  )
                                }
                                disabled={
                                  disabled
                                }
                                className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition ${
                                  disabled
                                    ? "text-gray-400 cursor-not-allowed"
                                    : isDark
                                    ? "text-yellow-400 hover:bg-yellow-900/30 hover:text-yellow-300"
                                    : "text-yellow-500 hover:bg-yellow-50 hover:text-yellow-600"
                                }`}
                                title={
                                  disabled
                                    ? "Scrapped item cannot be edited"
                                    : "Edit"
                                }
                              >
                                <FaEdit />
                              </button>
                            </td>

                            {/* TESTING */}

                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  handleTesting(
                                    card.id
                                  )
                                }
                                disabled={
                                  disabled
                                }
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  disabled
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : isDark
                                    ? "bg-blue-900/50 text-blue-300 hover:bg-blue-900"
                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                }`}
                                title={
                                  disabled
                                    ? "Scrapped item cannot be tested"
                                    : "Testing"
                                }
                              >
                                <FaTools />
                                Testing
                              </button>
                            </td>

                            {/* SCRAP */}

                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  doScrap(
                                    card.id
                                  )
                                }
                                disabled={
                                  disabled
                                }
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  disabled
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : isDark
                                    ? "bg-red-900/50 text-red-300 hover:bg-red-900"
                                    : "bg-red-100 text-red-700 hover:bg-red-200"
                                }`}
                                title={
                                  disabled
                                    ? "Already scrapped"
                                    : "Scrap"
                                }
                              >
                                <FaRecycle />
                                Scrap
                              </button>
                            </td>

                            {/* SELECT */}

                            <td className="px-4 py-4 text-center">
                              <input
                                type="checkbox"
                                checked={
                                  checked
                                }
                                onChange={() =>
                                  toggleSelect(
                                    card.id
                                  )
                                }
                                disabled={
                                  disabled
                                }
                                className="w-4 h-4 cursor-pointer"
                                title={
                                  disabled
                                    ? "Scrapped item cannot be selected"
                                    : "Select"
                                }
                              />
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>

                {/* =================================================
                    DELETE AREA
                ================================================= */}

                <div
                  className={`p-4 flex items-center justify-between flex-wrap gap-3 ${
                    isDark
                      ? "bg-gray-800"
                      : "bg-white"
                  }`}
                >
                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    {selectedIds.length >
                    0
                      ? `${selectedIds.length} record(s) selected`
                      : "No records selected"}
                  </div>

                  <button
                    type="button"
                    onClick={
                      doDelete
                    }
                    disabled={
                      selectedIds.length ===
                        0 ||
                      loading ||
                      pageLoading
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      selectedIds.length ===
                        0 ||
                      loading ||
                      pageLoading
                        ? "bg-gray-400 cursor-not-allowed text-white"
                        : "bg-red-600 hover:bg-red-700 text-white"
                    }`}
                  >
                    <FaTrash />

                    Delete Selected
                  </button>
                </div>
              </>
            )}
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          <div
            className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6`}
          >
            {/* PAGINATION LEFT */}

            <div className="flex items-center gap-3 flex-wrap">
              {/* PREV */}

              <button
                type="button"
                onClick={
                  goPrev
                }
                disabled={
                  page <= 1 ||
                  loading ||
                  pageLoading
                }
                className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                  page <= 1 ||
                  loading ||
                  pageLoading
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                } ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {/* PAGE */}

              <div
                className={`px-3 text-sm ${
                  isDark
                    ? "text-gray-300"
                    : "text-gray-700"
                }`}
              >
                Page{" "}
                <b>
                  {page}
                </b>{" "}
                /{" "}
                <b>
                  {totalPages}
                </b>
              </div>

              {/* NEXT */}

              <button
                type="button"
                onClick={
                  goNext
                }
                disabled={
                  page >=
                    totalPages ||
                  loading ||
                  pageLoading
                }
                className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                  page >=
                    totalPages ||
                  loading ||
                  pageLoading
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                } ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>

              {/* PAGE SIZE */}

              <select
                value={
                  limit
                }
                onChange={
                  changeLimit
                }
                disabled={
                  loading ||
                  pageLoading
                }
                className={`px-3 py-2 rounded-lg border text-sm ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              >
                {[
                  10,
                  20,
                  50,
                  100,
                ].map(
                  (x) => (
                    <option
                      key={x}
                      value={x}
                    >
                      {x} / page
                    </option>
                  )
                )}
              </select>
            </div>

            {/* PAGINATION INFO */}

            <div
              className={`text-sm ${
                isDark
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              Total:{" "}
              <span
                className={`font-semibold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                {total}
              </span>{" "}
              records
            </div>
          </div>

          {/* =================================================
              FOOTER
          ================================================= */}

          <div
            className={`mt-8 pt-6 border-t ${
              isDark
                ? "border-gray-700"
                : "border-gray-200"
            }`}
          >
            <p
              className={`text-sm text-center ${
                isDark
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              INFRA SCREEN : BATTERY CARD MAINTENANCE LISTING
            </p>
          </div>

          {/* =================================================
              TOAST
          ================================================= */}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={
              isDark
                ? "dark"
                : "light"
            }
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardMaintenanceListing;

